/**
 * @fileoverview Present bounded operational logs with filters, pagination, and recorded-detail inspection. The backend owns capture and permitted visibility; this view owns selected query and display. Log records are observations of particular attempts rather than a complete business ledger or authority to retry their effects.
 */

import React, { useContext, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import Button from "../shared/AppPrimeButton";
import { Tag } from "primereact/tag";
import api from "../../api";
import ToastContext from "../../ToastContext";
import { AppPageHeader } from "../shared";
import "./LogsPage.css";



function LogsPage() {
  // Purpose and fit:
  // - Provides a central Logs UI so the team can keep an eye on errors site wide
  //   and confirm successful automation and sync runs.
  // - Drives filtered log retrieval from the backend /api/logs endpoint.
  const showToast = useContext(ToastContext);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState({
    name: "",
    source: "",
    status: null,
    startDate: null,
    endDate: null,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    name: "",
    source: "",
    status: null,
    startDate: null,
    endDate: null,
  });

  const setPageTitle =

  () => {
    // Purpose and fit:
    // - Ensures the browser tab reflects the logs view for navigation clarity.
    document.title = "Logs";
  };

  const refreshLogsOnChange =

  () => {
    // Purpose and fit:
    // - Reloads logs whenever pagination or applied filters change.
    fetchLogs();
  };

  useEffect(setPageTitle, []);
  useEffect(refreshLogsOnChange, [page, pageSize, appliedFilters]);

  const formatDateParam =

  (date) => {
    // Purpose and fit:
    // - Converts JS Date objects into API-friendly YYYY-MM-DD strings so the
    //   backend can apply date range filters consistently.
    if (!date) {
      return null;
    }
    return date.toISOString().split("T")[0];
  };

  const buildQueryParams =

  () => {
    // Purpose and fit:
    // - Builds a stable query object for the /api/logs endpoint based on the
    //   user-applied filters and pagination settings.
    const params = {
      page,
      page_size: pageSize,
    };
    if (appliedFilters.name) {
      params.name = appliedFilters.name;
    }
    if (appliedFilters.source) {
      params.source = appliedFilters.source;
    }
    if (appliedFilters.status) {
      params.status = appliedFilters.status;
    }
    if (appliedFilters.startDate) {
      params.start_date = formatDateParam(appliedFilters.startDate);
    }
    if (appliedFilters.endDate) {
      params.end_date = formatDateParam(appliedFilters.endDate);
    }
    return params;
  };

  const fetchLogs =

  () => {
    // Purpose and fit:
    // - Retrieves log entries for the current filters and pagination so the
    //   Logs table stays accurate and up to date.
    setLoading(true);
    api
      .get("/logs", { params: buildQueryParams() })
      .then((response) => {
        if (response.status === 200) {
          setLogs(response.data.logs || []);
          setTotalLogs(response.data.total || 0);
        } else {
          showToast({
            severity: "error",
            summary: "Error",
            detail: "Unexpected status code",
          });
        }
      })
      .catch((error) => {
        console.error("Failed to fetch logs:", error);
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load logs",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNameChange =

  (event) => {
    // Purpose and fit:
    // - Updates the name filter so users can search for specific log entries.
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, name: value }));
  };

  const handleSourceChange =

  (event) => {
    // Purpose and fit:
    // - Updates the source filter so users can narrow logs to a subsystem
    //   like automations, sync_dir, sync_s3, or flask.
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, source: value }));
  };

  const handleStatusChange =

  (event) => {
    // Purpose and fit:
    // - Updates the status filter to quickly isolate successes or errors.
    const value = event.value;
    setFilters((prev) => ({ ...prev, status: value }));
  };

  const handleStartDateChange =

  (event) => {
    // Purpose and fit:
    // - Updates the start date filter for the log date range.
    const value = event.value;
    setFilters((prev) => ({ ...prev, startDate: value }));
  };

  const handleEndDateChange =

  (event) => {
    // Purpose and fit:
    // - Updates the end date filter for the log date range.
    const value = event.value;
    setFilters((prev) => ({ ...prev, endDate: value }));
  };

  const handleApplyFilters =

  () => {
    // Purpose and fit:
    // - Applies pending filter changes and resets pagination to the first page.
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters =

  () => {
    // Purpose and fit:
    // - Resets all filters so users can return to the full logs list quickly.
    const emptyFilters = {
      name: "",
      source: "",
      status: null,
      startDate: null,
      endDate: null,
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

  const handlePageChange =

  (event) => {
    // Purpose and fit:
    // - Keeps pagination state in sync with the DataTable controls.
    setPage(event.page + 1);
    setPageSize(event.rows);
  };

  const renderStatusTag =

  (rowData) => {
    // Purpose and fit:
    // - Renders a colored status chip so run outcomes are easy to scan.
    let severity = "danger";
    if (rowData.status === "success") {
      severity = "success";
    } else if (rowData.status === "queued") {
      severity = "info";
    } else if (rowData.status === "skipped") {
      severity = "warning";
    }
    return <Tag value={rowData.status} severity={severity} />;
  };

  const renderTimestamp =

  (rowData) => {
    // Purpose and fit:
    // - Formats timestamps for human-friendly display in the Logs table.
    if (!rowData.created_at) {
      return "";
    }
    return (
      <span className="app-data-value">
        {new Date(rowData.created_at).toLocaleString()}
      </span>
    );
  };

  const parseMetadata =

  (raw) => {
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const [expandedRows, setExpandedRows] = useState(null);

  const rowExpansionTemplate =

  (rowData) => {
    const meta = parseMetadata(rowData.metadata);
    if (!meta || Object.keys(meta).length === 0) {
      return (
        <div className="logs-entry-details logs-entry-details--empty">
          No additional details.
        </div>
      );
    }
    return (
      <section
        className="logs-entry-details"
        aria-label={`${rowData.name || "Log"} details`}
      >
        <dl className="logs-entry-details__list">
          {Object.entries(meta).map(([key, value]) => (
            <div key={key} className="logs-entry-details__item">
              <dt>{key}</dt>
              {key === "traceback" ? (
                <dd>
                  <pre className="logs-entry-details__traceback app-data-block">
                    {String(value)}
                  </pre>
                </dd>
              ) : (
                <dd className="app-data-value">
                  {typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value)}
                </dd>
              )}
            </div>
          ))}
        </dl>
      </section>
    );
  };

  const hasMetadata =

  (rowData) => {
    const meta = parseMetadata(rowData.metadata);
    return meta && Object.keys(meta).length > 0;
  };

  const statusOptions = [
    { label: "All", value: null },
    { label: "Queued", value: "queued" },
    { label: "Success", value: "success" },
    { label: "Skipped", value: "skipped" },
    { label: "Error", value: "error" },
  ];

  const activeFilterCount =
    Object.values(appliedFilters).filter(Boolean).length;

  return (
    <div className="logs-page">
      <AppPageHeader
        eyebrow="System Observability"
        title="Logs"
        icon="pi pi-list text-xl"
      />

      <main className="logs-page__content">
        <section className="logs-workspace" aria-label="System logs workspace">
          <section
            className="logs-filters"
            aria-labelledby="logs-filter-heading"
          >
            <header className="logs-section-heading">
              <span
                className="logs-section-heading__icon pi pi-filter"
                aria-hidden="true"
              />
              <div>
                <h2 id="logs-filter-heading">Filter logs</h2>
                <p>
                  Narrow the stream by operation, subsystem, outcome, or date.
                </p>
              </div>
              {activeFilterCount > 0 && (
                <span className="logs-filter-count">
                  {activeFilterCount} active
                </span>
              )}
            </header>

            <div className="logs-filters__grid">
              <div className="logs-filter-field">
                <label htmlFor="logs-name-filter">Name</label>
                <InputText
                  id="logs-name-filter"
                  value={filters.name}
                  onChange={handleNameChange}
                  className="w-full"
                />
              </div>
              <div className="logs-filter-field">
                <label htmlFor="logs-source-filter">Source</label>
                <InputText
                  id="logs-source-filter"
                  value={filters.source}
                  onChange={handleSourceChange}
                  className="w-full"
                />
              </div>
              <div className="logs-filter-field logs-filter-field--status">
                <label htmlFor="logs-status-filter">Status</label>
                <Dropdown
                  inputId="logs-status-filter"
                  value={filters.status}
                  options={statusOptions}
                  onChange={handleStatusChange}
                  className="w-full"
                  panelClassName="logs-filter-panel"
                  placeholder="All"
                />
              </div>
              <div className="logs-filter-field">
                <label htmlFor="logs-start-date-filter">Start date</label>
                <Calendar
                  inputId="logs-start-date-filter"
                  value={filters.startDate}
                  onChange={handleStartDateChange}
                  className="w-full"
                  panelClassName="logs-filter-panel"
                  dateFormat="yy-mm-dd"
                  showIcon
                />
              </div>
              <div className="logs-filter-field">
                <label htmlFor="logs-end-date-filter">End date</label>
                <Calendar
                  inputId="logs-end-date-filter"
                  value={filters.endDate}
                  onChange={handleEndDateChange}
                  className="w-full"
                  panelClassName="logs-filter-panel"
                  dateFormat="yy-mm-dd"
                  showIcon
                />
              </div>
            </div>

            <div className="logs-filters__actions">
              <Button
                label="Apply Filters"
                icon="pi pi-filter"
                onClick={handleApplyFilters}
              />
              <Button
                label="Clear"
                icon="pi pi-times"
                severity="secondary"
                onClick={handleClearFilters}
              />
            </div>
          </section>

          <section
            className="logs-stream"
            aria-labelledby="logs-stream-heading"
          >
            <header className="logs-section-heading logs-section-heading--stream">
              <span
                className="logs-section-heading__icon pi pi-list"
                aria-hidden="true"
              />
              <div>
                <h2 id="logs-stream-heading">Log stream</h2>
                <p>
                  {totalLogs.toLocaleString()}{" "}
                  {totalLogs === 1 ? "entry" : "entries"}
                  {activeFilterCount > 0
                    ? totalLogs === 1
                      ? " matches the applied filters"
                      : " match the applied filters"
                    : " available"}
                </p>
              </div>
            </header>

            <div className="logs-table-shell">
              <DataTable
                className="logs-table"
                value={logs}
                dataKey="id"
                lazy
                paginator
                rows={pageSize}
                totalRecords={totalLogs}
                first={(page - 1) * pageSize}
                onPage={handlePageChange}
                loading={loading}
                emptyMessage="No logs found"
                responsiveLayout="scroll"
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
              >
                <Column expander style={{ width: "3rem" }} />
                <Column
                  field="created_at"
                  header="Timestamp"
                  body={renderTimestamp}
                />
                <Column field="name" header="Name" />
                <Column field="status" header="Status" body={renderStatusTag} />
                <Column field="source" header="Source" />
                <Column field="message" header="Message" />
              </DataTable>
            </div>
          </section>
        </section>
      </main>
    </div>
  );
}

export default LogsPage;
